import { toAbsoluteUrl } from '../../utils';
import { Modal, Animation } from 'rsuite';
import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import './ModalWindow.scss';
import { ModalSize } from 'rsuite/Modal';
import { BUTTON_TYPE, ButtonComponent } from '../ButtonComponent';

export enum MODAL_TYPE {
	CANCEL,
	SAVE,
	SAVE_AND_SEND,
	DECLINE,
	APPROVE
}

export interface IModalWindow {
	onClose?: () => void;
	showCloseButton?: boolean;
	isOpen: boolean;
	size?: ModalSize;
	onConfirm?: () => void | Promise<void>;
	onCancel?: () => void | Promise<void>;
	content?: JSX.Element;
	modalText?: string;
	modalType?: MODAL_TYPE;
	modalTitle?: string;
	cancelButtonText?: string;
	confirmButtonText?: string;
	confirmDisabled?: boolean;
	minHeight?: number | string;
	className?: string;
	contentClassName?: string;
	backdrop?: boolean | 'static';
	isRightSideModal?: boolean;
	errorModal?: boolean;
	errorModalTitle?: JSX.Element | string;
	additionalConfirmButtonClass?: string;
	additionalCancelButtonClass?: string;
	isConfirmLoading?: boolean;
	isConfirmDisabled?: boolean;
	cypressAttribute?: string;
	enableOverflow?: boolean;
}

export const ModalWindow = (props: IModalWindow): JSX.Element => {
	const {
		onConfirm, onCancel, onClose, isOpen, modalText, backdrop = true,
		modalType, modalTitle, content, cancelButtonText, confirmButtonText,
		confirmDisabled, size, minHeight = 270, className, contentClassName,
		isRightSideModal, errorModal, errorModalTitle, showCloseButton = 'true',
		additionalConfirmButtonClass, additionalCancelButtonClass, isConfirmLoading = false, isConfirmDisabled,
		cypressAttribute = 'modal-window', enableOverflow = true
	} = props;
	let modalImage;

	const [isClicked, setIsClicked] = useState(false);

	useEffect(() => {
		setIsClicked(false);
	}, [isOpen]);

	switch (modalType) {
		case MODAL_TYPE.SAVE: {
			modalImage = (
				<img
					className="mb-8"
					src={toAbsoluteUrl('/media/icons/send-icon.svg')}
					height={48}
					width={48}
					alt=""
				/>
			);
			break;
		}
		case MODAL_TYPE.APPROVE: {
			modalImage = (
				<img
					className="mb-8"
					src={toAbsoluteUrl('/media/icons/check-circle-icon.svg')}
					height={48}
					width={48}
					alt=""
				/>
			);
			break;
		}
		case MODAL_TYPE.DECLINE: {
			modalImage = (
				<img
					className="mb-8"
					src={toAbsoluteUrl('/media/icons/exclamation-triangle-icon.svg')}
					height={48}
					width={48}
					alt=""
				/>
			);
			break;
		}
	}

	return (
		<Modal
			open={isOpen}
			size={size}
			onClose={(): void => showCloseButton ? onClose() : null}
			backdropClassName="backdrop-alignments"
			dialogClassName={clsx('dialog-alignments', className, isRightSideModal && 'h-100')}
			className={clsx(isRightSideModal ? 'modal-right-slide' : 'mb-10')}
			animation={isRightSideModal ? Animation.Slide : Animation.Fade}
			backdrop={backdrop}
			data-cy={cypressAttribute}
		>
			<div
				className={clsx(
					'modal-header d-flex align-items-center border-bottom-grey px-7 py-5',
					modalTitle || errorModalTitle ? 'justify-content-between' : 'justify-content-end',
					errorModal && 'error-modal',
				)}
				data-cy="modal-header"
			>
				{
					modalTitle && (
						<span className="fs-2 fw-bolder">
							{modalTitle}
						</span>
					)
				}
				{errorModalTitle && errorModalTitle}

				{showCloseButton && (
					<div
						className="w-30px h-30px d-flex align-items-center justify-content-center cursor-pointer p-3 modal-icon-container"
						onClick={onClose}
					>
						<FontAwesomeIcon icon={faXmark} className="modal-icon"/>
					</div>
				)}
			</div>
			<div
				className={clsx(
					'w-100 flex-column justify-content-center align-items-center',
					contentClassName ? contentClassName : 'd-flex',
					!isRightSideModal && enableOverflow && 'modal-max-height',
					isRightSideModal && enableOverflow && (onCancel || onConfirm) && 'side-modal-max-height-with-footer',
					isRightSideModal && enableOverflow && !(onCancel || onConfirm) && 'side-modal-max-height-without-footer',
				)}
				style={{ minHeight }}
				data-cy="modal-content"
			>
				{modalImage}
				{
					content
						? content
						: (
							<>
								<span className="fs-5 mx-15">
									{modalText}
								</span>
							</>
						)
				}
			</div>
			{
				(onCancel || onConfirm) && (
					<div className="w-100 d-flex justify-content-center align-items-center px-7 py-5" data-cy="modal-buttons">
						{
							onCancel && (
								<ButtonComponent
									buttonName={cancelButtonText ? cancelButtonText : 'Cancel'}
									buttonType={BUTTON_TYPE.CANCELLED}
									additionalClass={clsx(
										'btn-sm',
										!confirmButtonText && 'w-110px',
										additionalCancelButtonClass
									)}
									onButtonClick={async (): Promise<void> => {
										setIsClicked(true);
										await onCancel();
										setIsClicked(false);
									}}
									isDisabled={isClicked || isConfirmLoading}
                  data-cy="modal-cancel-button"
								/>
							)
						}

						{
							onConfirm && (
								<ButtonComponent
									buttonName={confirmButtonText ? confirmButtonText : 'Confirm'}
									additionalClass={clsx('btn-sm',
										onCancel && 'ms-10',
										!confirmButtonText && 'w-110px',
										additionalConfirmButtonClass
									)}
									onButtonClick={async (): Promise<void> => {
										setIsClicked(true);
										await onConfirm();
										setIsClicked(false);
									}}
									loading={isClicked || isConfirmLoading}
									isDisabled={isConfirmDisabled || isClicked || isConfirmLoading}
                  data-cy="modal-confirm-button"
								/>
							)
						}
					</div>
				)
			}

		</Modal>
	);
};
